/// /////////////////////////////////////////////////////////////////////////////////////
///                           Copyright (c) 2020 - 2025
///                                     RXO Inc.
///                          All Rights Reserved Worldwide
///
/// WARNING:  This program (or document) is unpublished, proprietary
/// property of RXO Inc. and is to be maintained in strict confidence.
/// Unauthorized reproduction, distribution or disclosure of this program
/// (or document), or any program (or document) derived from it is
/// prohibited by State and Federal law, and by local law outside of the U.S.
/// //////////////////////////////////////////////////////////////////////////////////////

import React, { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import ErrorMessage from "./ErrorMessage";
import SpinnerButton from "./SpinnerButton";
import {
  resetBannerActionState,
  downloadAwardedLanes,
  downloadShipperRatesAwardedLanes
} from "../../../slices/bidSummarySlice";
import messages from "../../../helpers/messages";
import ShipperRatesAwardTemplateOptionsModal from "../summary/ShipperRatesAwardTemplateOptionsModal";
import { getAwardedLanesV2 } from "../../../api/lanes";
import Alert from "../../Alert";
import { isEffectiveCoyoteBid } from "../../../helpers/isEffectiveCoyoteBid";

function UploadRatesState({ summary }) {
  const dispatch = useDispatch();
  const {
    isAwardTemplateDownloading,
    awardTemplateDownloadFailed,
    shipperRatesEnabled
  } = useSelector(state => state.bidSummary);
  const { bidId, name } = summary;
  const effectiveCoyoteBid = isEffectiveCoyoteBid(summary);
  const [
    openShipperRatesAwardTemplateOptionsModal,
    setOpenShipperRatesAwardTemplateOptionsModal
  ] = useState(false);
  const [downloadFOTemplateProgress, toggleDownloadFOTemplateProgress] =
    useState(false);
  const [downloadFOTemplateError, toggleDownloadFOTemplateError] =
    useState(false);
  const [
    showAwardManagementDownloadInProgressModal,
    setShowAwardManagementDownloadInProgressModal
  ] = useState(false);

  const handleDownloadTemplate = useCallback(
    bidId => {
      const fileName = `${name} Rates.xlsm`;
      dispatch(resetBannerActionState());
      dispatch(downloadAwardedLanes(bidId, fileName));
    },
    [dispatch, name]
  );

  const handleDownloadShipperRatesTemplate = settings => {
    const fileName = `${name} Rates.xlsx`;
    dispatch(resetBannerActionState());
    dispatch(downloadShipperRatesAwardedLanes(settings, fileName));
  };

  const handleDownloadFOTemplate = () => {
    toggleDownloadFOTemplateProgress(true);
    getAwardedLanesV2(bidId)
      .then(() => {
        toggleDownloadFOTemplateProgress(false);
        setShowAwardManagementDownloadInProgressModal(true);
      })
      .catch(() => {
        toggleDownloadFOTemplateProgress(false);
        toggleDownloadFOTemplateError(true);
      });
  };

  const handleClose = close => {
    setOpenShipperRatesAwardTemplateOptionsModal(close);
  };

  return (
    <>
      <p className="inline-block font-size-14 pt-16 mb-0">
        {awardTemplateDownloadFailed || downloadFOTemplateError ? (
          <ErrorMessage />
        ) : !effectiveCoyoteBid ? (
          messages.awardsHaveBeenImportedRXOBidMessage.defaultMessage
        ) : shipperRatesEnabled ? (
          messages.awardsHaveBeenImportedShipperRatesMessage.defaultMessage
        ) : (
          messages.awardsHaveBeenImportedMessage.defaultMessage
        )}
      </p>
      <div className="fr py-8 my-2">
        {isAwardTemplateDownloading || downloadFOTemplateProgress ? (
          <SpinnerButton minWidth="152px" />
        ) : (
          <button
            className="text-button univers-67-bold-cond font-size-14 uppercase mx-8 h-32 px-8"
            data-testid="download-template"
            onClick={() =>
              !effectiveCoyoteBid
                ? handleDownloadFOTemplate()
                : shipperRatesEnabled
                ? setOpenShipperRatesAwardTemplateOptionsModal(true)
                : handleDownloadTemplate(bidId)
            }
          >
            {messages.downloadTemplate.defaultMessage}
          </button>
        )}
      </div>
      {openShipperRatesAwardTemplateOptionsModal && (
        <ShipperRatesAwardTemplateOptionsModal
          bidId={bidId}
          handleClose={handleClose}
          handleDownload={handleDownloadShipperRatesTemplate}
        />
      )}
      {showAwardManagementDownloadInProgressModal && (
        <Alert
          title={messages.processing.defaultMessage}
          message={messages.awardExportInProgressModalMessage.defaultMessage}
          handleClose={() =>
            setShowAwardManagementDownloadInProgressModal(false)
          }
          open={showAwardManagementDownloadInProgressModal}
          large={true}
        />
      )}
    </>
  );
}

export default UploadRatesState;
